import { useCallback, useEffect, useRef, useState } from 'react';
import { useDiscoveryContext } from '../context/DiscoveryContext';
import { generateWireframe } from '../api/discoveryApi';

const DEBOUNCE_MS = 2000;
const MIN_USER_MESSAGES_BETWEEN = 3;

function countUserMessages(messages) {
  if (!Array.isArray(messages)) return 0;
  let count = 0;
  for (const m of messages) {
    if (m && m.role === 'user') count += 1;
  }
  return count;
}

export function useWireframe() {
  const { state, dispatch } = useDiscoveryContext();
  const [wireframe, setWireframe] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const userMessagesAtLastGenRef = useRef(-MIN_USER_MESSAGES_BETWEEN);
  const versionRef = useRef(0);
  const sessionIdRef = useRef(state.sessionId);
  sessionIdRef.current = state.sessionId;

  const cleanup = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const runGeneration = useCallback(
    async (messages) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setStatus('generating');
      setError(null);
      dispatch({ type: 'WIREFRAME_START' });

      try {
        const result = await generateWireframe(sessionId, messages, {
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        if (!result || !result.image) {
          throw new Error(result?.error || 'generation_failed');
        }

        versionRef.current += 1;
        const payload = {
          image: result.image,
          description: result.description || '',
          version: result.version ?? versionRef.current,
          model: result.model || null,
          createdAt: Date.now(),
        };
        setWireframe(payload);
        setStatus('complete');
        dispatch({ type: 'WIREFRAME_COMPLETE', payload });
        dispatch({
          type: 'ADD_ASSISTANT_MESSAGE',
          payload: {
            content: "Here's a rough sketch based on what we've discussed:",
            type: 'wireframe',
            image: payload.image,
            wireframeVersion: payload.version,
          },
        });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setStatus('error');
        setError(err.message || 'generation_failed');
        dispatch({ type: 'WIREFRAME_FAILED', payload: err.message || 'generation_failed' });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [dispatch],
  );

  const generate = useCallback(
    (messages) => {
      if (!sessionIdRef.current) return;
      if (!Array.isArray(messages) || messages.length < 2) return;

      const userCount = countUserMessages(messages);
      if (userCount - userMessagesAtLastGenRef.current < MIN_USER_MESSAGES_BETWEEN) {
        return;
      }

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setStatus('debouncing');

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        userMessagesAtLastGenRef.current = userCount;
        runGeneration(messages);
      }, DEBOUNCE_MS);
    },
    [runGeneration],
  );

  const manualGenerate = useCallback(
    (messages) => {
      if (!sessionIdRef.current) return;
      if (!Array.isArray(messages) || messages.length < 2) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      userMessagesAtLastGenRef.current = countUserMessages(messages);
      runGeneration(messages);
    },
    [runGeneration],
  );

  useEffect(() => cleanup, [cleanup]);

  return {
    wireframe,
    status,
    error,
    generate,
    manualGenerate,
    cleanup,
  };
}
